/**
 * PartnerProfileForm — E-015 Phase 2 Partner Profile metadata editor.
 *
 * Sibling to FormatSettingsForm. Presentational only: no API calls.
 * Captures four descriptive fields on an ExportFormat:
 *   - fileNamingPattern  — a naming template. Default for all destinations
 *                          using this format; a destination's own Filename
 *                          template overrides it (Task 4).
 *   - partnerNotes       — free-text operator context
 *   - schemaAttachment   — PROVENANCE ONLY (Task 5): the original partner
 *                          schema (XSD / JSON-Schema / EDI dict) a format's
 *                          conformance contract was imported from, or a manual
 *                          paste. Stored blob, never validated or enforced —
 *                          enforcement lives solely in `conformanceContract`.
 *   - sampleData         — sample/preview blob; feeds the layout preview for
 *                          columns whose names match (Task 6), else falls
 *                          back to synthesized placeholder values
 *
 * schemaAttachment / sampleData may be stored as a string OR a JSON value.
 * For editing we surface them as text (objects are pretty-printed for display).
 * On change they are emitted as raw strings — the server accepts strings; if an
 * operator pastes JSON it is stored verbatim as a string, which round-trips fine.
 */
import { TemplateInput } from "../../components/TemplateInput";

export interface PartnerProfileValue {
  fileNamingPattern: string;
  partnerNotes: string;
  schemaAttachment: string;
  sampleData: string;
}

export interface PartnerProfileFormProps {
  value: PartnerProfileValue;
  onChange: (next: PartnerProfileValue) => void;
}

/** Collapse a blank (whitespace-only) string to null; otherwise pass through.
 *  Shared by the wizard and detail-page save handlers so an empty Partner
 *  Profile field is persisted as null rather than "". */
export function orNull(s: string): string | null {
  return s.trim() === '' ? null : s;
}

/** Coerce a stored field (string | JSON | null | undefined) into editable text. */
export function partnerFieldToText(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

export function PartnerProfileForm({ value, onChange }: PartnerProfileFormProps) {
  const set = (key: keyof PartnerProfileValue, val: string) =>
    onChange({ ...value, [key]: val });

  return (
    <div className="format-settings">
      <TemplateInput
        id="pp-file-naming"
        label="File Naming Pattern"
        value={value.fileNamingPattern}
        placeholder="{batch.name}_{timestamp}_{seq}.csv"
        onChange={(next) => set('fileNamingPattern', next)}
      />
      <p className="rb-hint" style={{ marginBottom: 'var(--space-3)' }}>
        Default for all destinations using this format; a destination's own
        Filename template overrides it.
      </p>

      <div className="rb-row" style={{ alignItems: 'flex-start' }}>
        <label className="rb-label" htmlFor="pp-partner-notes">Partner Notes</label>
        <textarea
          id="pp-partner-notes"
          className="rb-input"
          rows={3}
          style={{ resize: 'vertical' }}
          value={value.partnerNotes}
          placeholder="Operator-facing context (delivery window, contacts, quirks…)"
          onChange={(e) => set('partnerNotes', e.target.value)}
        />
      </div>

      <div className="rb-row" style={{ alignItems: 'flex-start' }}>
        <label className="rb-label" htmlFor="pp-schema">Source schema (provenance)</label>
        <textarea
          id="pp-schema"
          className="rb-input"
          rows={4}
          style={{ resize: 'vertical', fontFamily: 'var(--font-mono, monospace)' }}
          value={value.schemaAttachment}
          placeholder="Paste XSD / JSON Schema / EDI dictionary (stored as-is, not validated)"
          onChange={(e) => set('schemaAttachment', e.target.value)}
        />
      </div>
      <p className="rb-hint" style={{ marginBottom: 'var(--space-3)' }}>
        The original partner schema this format's conformance contract was
        imported from. Pasting here does NOT enforce anything — use Import
        Partner Schema for enforcement.
      </p>

      <div className="rb-row" style={{ alignItems: 'flex-start' }}>
        <label className="rb-label" htmlFor="pp-sample">Sample Data</label>
        <textarea
          id="pp-sample"
          className="rb-input"
          rows={4}
          style={{ resize: 'vertical', fontFamily: 'var(--font-mono, monospace)' }}
          value={value.sampleData}
          placeholder='Paste sample rows, e.g. [{"invoiceNo": "INV-1", "amount": "99.10"}]'
          onChange={(e) => set('sampleData', e.target.value)}
        />
      </div>
      <p className="rb-hint" style={{ marginBottom: 'var(--space-3)' }}>
        Used by the layout preview when it matches the column names; JSON
        array of row objects.
      </p>
    </div>
  );
}
